'use client';

import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import StarRatingInput from './StarRatingInput';

const TONES = ['Professional', 'Friendly', 'Apologetic', 'Grateful'] as const;
type Tone = typeof TONES[number];
type Platform = 'Google Reviews' | 'TripAdvisor';

// Mock AI responses for the demo widget — backend: replace with Anthropic streaming API call
const MOCK_RESPONSES: Record<string, string> = {
  '1-Professional': `Dear ${'{reviewer}'},\n\nThank you for taking the time to share your experience at ${'{restaurant}'}. I'm genuinely sorry that your visit fell short of what we strive to deliver — the service delay you described is not something we consider acceptable, and I want you to know your feedback has been shared directly with our front-of-house team.\n\nWe'd very much like the chance to welcome you back and show you the standard we hold ourselves to. Please reach out to us directly at hello@${'{restaurant}'.toLowerCase().replace(/\s/g, '')}.co.uk and I'll personally ensure your next visit is a very different experience.\n\nWarm regards,\nThe Team at ${'{restaurant}'}`,
  '1-Apologetic': `Dear ${'{reviewer}'},\n\nI'm so sorry — this is not the experience we want for any of our guests, and reading your review has genuinely upset me. A wait of that length with no explanation is simply not good enough, and I take full responsibility for that.\n\nPlease accept my sincere apologies. If you'd be willing to give us another chance, I'd love to personally make it right. Do get in touch — your next meal is on us.\n\nWith apologies and warmth,\nThe Team at ${'{restaurant}'}`,
  '4-Friendly': `Hi ${'{reviewer}'}! 😊\n\nWhat a lovely review to wake up to — thank you so much! We're absolutely thrilled that the truffle pasta hit the spot (it's a firm favourite with our regulars too). Your kind words about Sofia will genuinely make her week.\n\nWe'd love to see you back at ${'{restaurant}'} very soon — next time you're in, make sure to ask about our weekend tasting menu. We think you'll love it.\n\nSee you soon!\nThe Team at ${'{restaurant}'}`,
  '5-Grateful': `Dear ${'{reviewer}'},\n\nThis has made our whole team smile — thank you so much for such a warm and generous review. Moments like these are exactly why we do what we do.\n\nWe're so glad the birthday dinner was everything you'd hoped for. It was our absolute pleasure to be part of such a special evening, and we hope ${'{restaurant}'} becomes your go-to for every celebration to come.\n\nWith heartfelt gratitude,\nThe Team at ${'{restaurant}'}`,
};

function getMockResponse(rating: number, tone: Tone, restaurantName: string): string {
  const key = `${rating}-${tone}`;
  const base = MOCK_RESPONSES[key] || MOCK_RESPONSES['4-Friendly'] || '';
  return base
    .replace(/\{restaurant\}/g, restaurantName || 'our restaurant')
    .replace(/\{reviewer\}/g, 'Valued Guest');
}

export default function LiveAppWidget() {
  const [platform, setPlatform] = useState<Platform>('Google Reviews');
  const [rating, setRating] = useState(3);
  const [reviewText, setReviewText] = useState('');
  const [tone, setTone] = useState<Tone>('Professional');
  const [restaurantName, setRestaurantName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReply, setGeneratedReply] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);
  const [usesRemaining, setUsesRemaining] = useState(5);
  const outputRef = useRef<HTMLDivElement>(null);

  const simulateStreaming = (fullText: string) => {
    const words = fullText.split(' ');
    let currentIndex = 0;
    setGeneratedReply('');
    setIsGenerating(true);

    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        const chunk = words.slice(0, currentIndex + 1).join(' ');
        setGeneratedReply(chunk);
        currentIndex++;
        if (outputRef.current) {
          outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
      } else {
        clearInterval(interval);
        setIsGenerating(false);
        setHasGenerated(true);
      }
    }, 45);
  };

  const handleGenerate = () => {
    if (!reviewText.trim()) {
      toast.error('Please paste a review before generating a reply.');
      return;
    }
    if (!restaurantName.trim()) {
      toast.error('Please enter your restaurant name.');
      return;
    }
    if (usesRemaining <= 0) {
      toast.error('You\'ve used all 5 free replies. Sign up to continue.');
      return;
    }

    setUsesRemaining((prev) => prev - 1);
    const response = getMockResponse(rating, tone, restaurantName);
    simulateStreaming(response);
    // Backend integration point: POST /api/generate-reply with { platform, rating, reviewText, tone, restaurantName }
    // Then use ReadableStream / SSE to stream Claude's response word-by-word
  };

  const handleCopy = () => {
    if (!generatedReply) return;
    navigator.clipboard.writeText(generatedReply).then(() => {
      setCopyFlash(true);
      toast.success('Reply copied to clipboard!');
      setTimeout(() => setCopyFlash(false), 1200);
    });
  };

  const handleRegenerate = () => {
    if (!reviewText.trim() || !restaurantName.trim()) return;
    const response = getMockResponse(rating, tone, restaurantName);
    simulateStreaming(response);
    // Backend integration point: same as handleGenerate — re-call the API with same inputs
  };

  const usagePercent = ((5 - usesRemaining) / 5) * 100;

  return (
    <div className="card-elevated-lg max-w-5xl mx-auto overflow-hidden">
      {/* Widget header */}
      <div
        className="px-6 py-4 flex items-center justify-between border-b"
        style={{ backgroundColor: 'var(--secondary)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF5F57' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FEBC2E' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#28C840' }} />
          <span className="ml-3 text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
            ReviewReply AI — Live Demo
          </span>
        </div>
        <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(193, 68, 14, 0.1)', color: 'var(--primary)' }}>
          No account needed
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x" style={{ borderColor: 'var(--border)' }}>
        {/* LEFT PANEL — Input form */}
        <div className="p-6 space-y-5">
          <div>
            <p className="section-label mb-3">Paste your review</p>

            {/* Platform selector */}
            <div className="mb-4">
              <label className="form-label">Review Platform</label>
              <div className="flex gap-2">
                {(['Google Reviews', 'TripAdvisor'] as Platform[]).map((p) => (
                  <button
                    key={`platform-${p}`}
                    onClick={() => setPlatform(p)}
                    className="flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-150 border"
                    style={{
                      backgroundColor: platform === p ? 'var(--primary)' : 'var(--input)',
                      color: platform === p ? '#FDFAF6' : 'var(--muted-foreground)',
                      borderColor: platform === p ? 'var(--primary)' : 'var(--border)',
                    }}
                  >
                    {p === 'Google Reviews' ? '🔵 Google' : '🟢 TripAdvisor'}
                  </button>
                ))}
              </div>
            </div>

            {/* Star rating */}
            <div className="mb-4">
              <label className="form-label">Star Rating</label>
              <StarRatingInput value={rating} onChange={setRating} />
            </div>

            {/* Review textarea */}
            <div className="mb-4">
              <label className="form-label">Review Text</label>
              <textarea
                className="form-input resize-none"
                rows={4}
                placeholder="Paste the customer's review here…"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
            </div>

            {/* Tone selector */}
            <div className="mb-4">
              <label className="form-label">Response Tone</label>
              <div className="grid grid-cols-2 gap-2">
                {TONES.map((t) => (
                  <button
                    key={`tone-${t}`}
                    onClick={() => setTone(t)}
                    className="py-2 px-3 rounded-lg text-sm font-medium transition-all duration-150 border"
                    style={{
                      backgroundColor: tone === t ? 'rgba(193, 68, 14, 0.08)' : 'var(--input)',
                      color: tone === t ? 'var(--primary)' : 'var(--muted-foreground)',
                      borderColor: tone === t ? 'var(--primary)' : 'var(--border)',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Restaurant name */}
            <div className="mb-5">
              <label className="form-label">Your Restaurant Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. The Amber Kitchen"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
              />
            </div>

            {/* Usage meter */}
            <div
              className="rounded-lg p-3 mb-4"
              style={{ backgroundColor: usesRemaining <= 1 ? 'rgba(220, 38, 38, 0.06)' : 'var(--muted)' }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium" style={{ color: usesRemaining <= 1 ? '#DC2626' : 'var(--muted-foreground)' }}>
                  {usesRemaining > 0 ? `${usesRemaining} of 5 free uses remaining` : 'Free uses exhausted — sign up to continue'}
                </span>
                {usesRemaining <= 1 && (
                  <span className="text-xs font-semibold" style={{ color: '#DC2626' }}>⚠ Low</span>
                )}
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${usagePercent}%`,
                    background: usesRemaining <= 1
                      ? 'linear-gradient(90deg, #DC2626, #EF4444)'
                      : 'linear-gradient(90deg, var(--primary), var(--accent))',
                  }}
                />
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={isGenerating || usesRemaining <= 0}
              className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Crafting your reply…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  Generate Reply
                </>
              )}
            </button>
          </div>
        </div>

        {/* RIGHT PANEL — Output */}
        <div className="p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <p className="section-label">AI-Generated Reply</p>
            {hasGenerated && !isGenerating && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRegenerate}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-150"
                  style={{ color: 'var(--muted-foreground)', borderColor: 'var(--border)' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--primary)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--primary)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--muted-foreground)';
                  }}
                >
                  ↻ Regenerate
                </button>
                <button
                  onClick={handleCopy}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150"
                  style={{
                    backgroundColor: copyFlash ? 'rgba(34, 197, 94, 0.15)' : 'var(--primary)',
                    color: copyFlash ? '#16A34A' : '#FDFAF6',
                  }}
                >
                  {copyFlash ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>

          <div
            ref={outputRef}
            className="flex-1 rounded-xl p-5 min-h-64 overflow-y-auto scrollbar-thin"
            style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            {!generatedReply && !isGenerating ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: 'rgba(193, 68, 14, 0.1)' }}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--primary)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>
                  Your reply will appear here
                </p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Paste a review on the left and hit Generate Reply
                </p>
              </div>
            ) : (
              <div
                className="text-sm leading-relaxed whitespace-pre-line"
                style={{ color: 'var(--foreground)' }}
              >
                {generatedReply}
                {isGenerating && (
                  <span
                    className="inline-block w-0.5 h-4 ml-0.5 align-middle animate-blink"
                    style={{ backgroundColor: 'var(--primary)' }}
                  />
                )}
              </div>
            )}
          </div>

          {!hasGenerated && (
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                'Sounds human, not corporate',
                'Specific to the review',
                'No "valued feedback" clichés',
              ].map((chip) => (
                <span
                  key={`chip-${chip}`}
                  className="text-xs px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: 'rgba(193, 68, 14, 0.08)', color: 'var(--primary)' }}
                >
                  ✓ {chip}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}